import { useCallback, useRef, useState } from "react";

interface Memory {
  revealed: ReadonlySet<string>;
  open: ReadonlySet<string>;
}

const EMPTY: Memory = { revealed: new Set(), open: new Set() };

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === "string");

const keyOf = (lectureId: string) => `prep:v1:${lectureId}`;

function load(lectureId: string): Memory {
  try {
    const raw = localStorage.getItem(keyOf(lectureId));
    if (raw === null) return EMPTY;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return EMPTY;
    const { revealed, open } = parsed as Record<string, unknown>;
    if (!isStringArray(revealed) || !isStringArray(open)) return EMPTY;
    return { revealed: new Set(revealed), open: new Set(open) };
  } catch {
    return EMPTY;
  }
}

function save(lectureId: string, memory: Memory): void {
  try {
    localStorage.setItem(
      keyOf(lectureId),
      JSON.stringify({ revealed: [...memory.revealed], open: [...memory.open] }),
    );
  } catch {
    // Storage may be blocked or full; the page works without memory.
  }
}

function flip(set: ReadonlySet<string>, id: string): ReadonlySet<string> {
  const next = new Set(set);
  if (!next.delete(id)) next.add(id);
  return next;
}

/** Per-browser memory of revealed derivation steps and opened panels for one lecture. */
export function usePrepMemory(lectureId: string): {
  revealed: ReadonlySet<string>;
  toggleRevealed(stepId: string): void;
  openPanels: ReadonlySet<string>;
  togglePanel(id: string): void;
} {
  const [state, setState] = useState(() => ({ lectureId, memory: load(lectureId) }));
  let current = state;
  if (state.lectureId !== lectureId) {
    current = { lectureId, memory: load(lectureId) };
    setState(current);
  }
  const ref = useRef(current);
  ref.current = current;

  const update = useCallback((change: (memory: Memory) => Memory) => {
    const { lectureId: id, memory } = ref.current;
    const next = { lectureId: id, memory: change(memory) };
    ref.current = next;
    setState(next);
    save(id, next.memory);
  }, []);

  const toggleRevealed = useCallback(
    (stepId: string) => update((m) => ({ ...m, revealed: flip(m.revealed, stepId) })),
    [update],
  );
  const togglePanel = useCallback(
    (id: string) => update((m) => ({ ...m, open: flip(m.open, id) })),
    [update],
  );

  return {
    revealed: current.memory.revealed,
    toggleRevealed,
    openPanels: current.memory.open,
    togglePanel,
  };
}
